import { useCallback, useMemo } from 'react'

import { $api } from '#/api/client'
import { isRecord } from '#/utils/typeGuards'
import { E_TICKET_PRODUCT_KEYS, type TProductKey } from '#interfaces/contract'

const CARD_CATEGORY_KEYS = new Set<string>(['personal_cards', 'shared_cards', 'day_cards'])
const OTHER_PRODUCT_KEYS = new Set<string>([...E_TICKET_PRODUCT_KEYS, 'saldo_laden'])
const ALL_PRODUCT_KEYS = new Set<string>([...CARD_CATEGORY_KEYS, ...OTHER_PRODUCT_KEYS])

function isTProductKey(value: string): value is TProductKey {
  return ALL_PRODUCT_KEYS.has(value)
}

function hasEnabledBrandProduct(bpSettings: unknown): boolean {
  if (!isRecord(bpSettings)) return false

  return Object.values(bpSettings).some((bp) => isRecord(bp) && bp.enabled === true)
}

function deriveAllowedProducts(productSettings: unknown): TProductKey[] {
  if (!isRecord(productSettings)) return []

  const products = isRecord(productSettings.products) ? productSettings.products : productSettings
  const result: TProductKey[] = []

  for (const [key, value] of Object.entries(products)) {
    if (!isTProductKey(key) || !isRecord(value)) continue

    if (CARD_CATEGORY_KEYS.has(key)) {
      if (hasEnabledBrandProduct(value.brand_product_settings)) result.push(key)
    } else if (OTHER_PRODUCT_KEYS.has(key)) if (value.enabled === true) result.push(key)
  }

  // e_tickets and printed_tickets live at the top level of productSettings (not under products)
  for (const topLevelKey of E_TICKET_PRODUCT_KEYS) {
    const topLevelProduct = productSettings[topLevelKey]
    if (typeof topLevelProduct === 'object' && topLevelProduct !== null)
      if ((topLevelProduct as Record<string, unknown>).enabled === true) result.push(topLevelKey)
  }

  return result
}

interface ICompanyContractCapabilities {
  allowedProducts: TProductKey[]
  isProductEnabled: (key: TProductKey) => boolean
  isLoading: boolean
}

export const useCompanyContractCapabilities = (companyId: string): ICompanyContractCapabilities => {
  const { data, isLoading } = $api.useQuery(
    'get',
    '/companies/{companyId}/contracts',
    {
      params: {
        path: { companyId },
      },
    },
    {
      select: (response): TProductKey[] => {
        const contracts = response.member ?? []
        const first = contracts[0]
        if (!first) return []

        return deriveAllowedProducts((first as Record<string, unknown>).productSettings)
      },
    },
  )

  const allowedProducts = useMemo((): TProductKey[] => data ?? [], [data])

  const isProductEnabled = useCallback((key: TProductKey): boolean => allowedProducts.includes(key), [allowedProducts])

  return {
    allowedProducts,
    isProductEnabled,
    isLoading,
  }
}
